// src/components/kuestion/shared/display.js
//
// The display settings every rendered time depends on: the effective zone and
// the clock (Preferences & Timezone contract v1.2, §7.1 and §13). Kept in a
// module store so the plain formatting helpers in format.js can read it
// without every screen threading a context through; PreferencesContext writes
// it and bumps a version so the screens re-render.
//
// Resolution, first hit wins:
//   1. the user's explicit choice from GET /api/me/preferences
//   2. the device zone — the normal path, not a fallback
//   3. for a student whose browser reports nothing usable, the org default
//   4. UTC, and the label says so

const state = { timezone: null, orgDefault: null, clock: '24', fallbackUtc: false };
const listeners = new Set();

export const deviceZone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch { return null; }
};

export const isValidZone = (id) => {
  if (!id || typeof id !== 'string') return false;
  try { new Intl.DateTimeFormat('en-GB', { timeZone: id }); return true; } catch { return false; }
};

export function effectiveZone() {
  if (state.timezone && isValidZone(state.timezone)) return state.timezone;
  const dev = deviceZone();
  if (dev && isValidZone(dev)) return dev;
  if (state.orgDefault && isValidZone(state.orgDefault)) return state.orgDefault;
  return 'UTC';
}

export const getDisplay = () => ({ ...state, zone: effectiveZone() });

export function setDisplay(patch) {
  Object.assign(state, patch);
  listeners.forEach((fn) => { try { fn(getDisplay()); } catch { /* listener */ } });
}

export const subscribeDisplay = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

// Offset label read from the platform, e.g. "GMT+4", so DST is right without
// a table. For UTC itself the platform says "GMT"; the contract wants "UTC".
export function zoneOffset(id = effectiveZone(), at = new Date()) {
  try {
    const p = new Intl.DateTimeFormat('en-GB', { timeZone: id, timeZoneName: 'shortOffset' }).formatToParts(at);
    const v = (p.find((x) => x.type === 'timeZoneName') || {}).value || '';
    return v === 'GMT' && id === 'UTC' ? 'UTC' : v;
  } catch { return ''; }
}

// Minutes east of UTC for a zone at an instant, from the same platform label.
export function zoneOffsetMinutes(id, at = new Date()) {
  const m = /GMT([+-])(\d{1,2})(?::(\d{2}))?/.exec(zoneOffset(id, at) || '');
  return m ? (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3] || 0)) : 0;
}

// "Dubai" from "Asia/Dubai"; "Ho Chi Minh" from "Asia/Ho_Chi_Minh".
export const zoneCity = (id) => String(id || '').split('/').pop().replace(/_/g, ' ');

// "Dubai · GMT+4"
export const zoneLabelFor = (id) => `${zoneCity(id)} · ${zoneOffset(id)}`;

// Calendar fields of an instant in a zone, as numbers.
export function partsIn(d, zone = effectiveZone()) {
  const f = new Intl.DateTimeFormat('en-GB', {
    timeZone: zone, hour12: false, weekday: 'short',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  });
  const o = {};
  f.formatToParts(d).forEach((p) => { if (p.type !== 'literal') o[p.type] = p.value; });
  return {
    year: Number(o.year), month: Number(o.month), day: Number(o.day),
    hour: Number(o.hour) % 24, minute: Number(o.minute),
    weekday: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(o.weekday),
  };
}

// "17:00" or "5:00 pm", per the clock preference, in a zone.
export function clockIn(d, zone = effectiveZone(), clock = state.clock) {
  const { hour, minute } = partsIn(d, zone);
  const mm = String(minute).padStart(2, '0');
  if (clock === '12') {
    const h = hour % 12 || 12;
    return `${h}:${mm} ${hour < 12 ? 'am' : 'pm'}`;
  }
  return `${String(hour).padStart(2, '0')}:${mm}`;
}

// The instant at which a wall-clock time in a zone occurs. Two passes, so the
// offset is the one in force on that date rather than today's.
export function instantOf(year, month, day, hour, minute, zone = effectiveZone()) {
  const asUtc = Date.UTC(year, month - 1, day, hour, minute);
  let d = new Date(asUtc - zoneOffsetMinutes(zone, new Date(asUtc)) * 60000);
  d = new Date(asUtc - zoneOffsetMinutes(zone, d) * 60000);
  return d;
}

// A short curated list, not all 400-odd zones: the ones this product is sold
// into, plus whatever the device reports (prepended by the menu). No Etc/*:
// the sign is inverted there and the menu would read as broken (§12).
export const CURATED_ZONES = ['UTC', 'Europe/London', 'Europe/Berlin', 'Africa/Lagos', 'Africa/Nairobi',
  'Asia/Dubai', 'Asia/Karachi', 'Asia/Kolkata', 'Asia/Dhaka', 'Asia/Singapore', 'Asia/Tokyo',
  'America/New_York', 'America/Chicago', 'America/Los_Angeles', 'Australia/Sydney', 'Pacific/Auckland'];
